import { useCallback, useState } from "react";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { processError } from "../../../../common/utils/base_error";
import { useNavigate } from "react-router";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { useFuncionariosTercerizacaoFilters } from "../hooks/Funcionarios_tercerizacao_lista/filters";
import { useFuncionariosTercerizacaoPagination } from "../hooks/Funcionarios_tercerizacao_lista/pagination";
import { FuncionarioTercLista } from "../../domain/models/funcionarios_tercerizado_lista";

/**
 * LoadFuncionarioTercState
 */

type LoadFuncionarioTercState = {
  IdFuncionarioTerc?:number,
  NomePesquisa?: string;
  FornecedorPesquisa?: number;
  ContratoPesquisa?: number;
  CargoPesquisa?: number;
  centroDeCustoLista?: { id: number; title: string }[];
  centroDeCusto?: number[];
  StatusPesquisa?: number;
  AvaliacoesPesquisa?: number;
  DocumentosPesquisa?: number;
  page?: number;
  rowsPerPage?: number;
};

/**
 * useGestaoTreinamentosListaViewModel
 */

export const useGestaoTreinamentosListaViewModel = () => {
  const {
    commons: {
      getCentroCustoDropDownUseCase,
    },
    terceirizacao: {getAllListagemFuncionariosTercUseCase,
      deleteFuncionariosTercUseCase,
    },
  } = useDependencyInjection();

  const debouncedRef = useCreateDebounceHandler(750);

  const { pushErrorNotification } = usePageLoader();

  const [errorMessage, setErrorMessage] = useState<string>();
  const [funcionarios, setFuncionarios] = useState<FuncionarioTercLista[]>();
  const [loading, setLoading] = useState(false);
  const [selectedFuncionario, setSelectedFuncionario] = useState<FuncionarioTercLista>();
  const [showLegenda, setShowLegenda] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteModal, setDeleteModal] = useState<boolean>(false);


  const navigate = useNavigate();

  const filters = useFuncionariosTercerizacaoFilters();
  const pagination = useFuncionariosTercerizacaoPagination();

  /**
   * Funcoes de carregamento
   */

  const loadCentroCustoDropDown = useCallback(async () => {
    try {
      const centroCusto = await getCentroCustoDropDownUseCase.getCentroCustoDropDown();
      filters.setCentroDeCustoLista([...centroCusto]);
    } catch (e: unknown) {
      throw e;
    }
  }, [filters, getCentroCustoDropDownUseCase]);

  const loadFuncionarioTerc = useCallback(async (funcState?: LoadFuncionarioTercState) => {
    try {
      setLoading(true);
      const filteredIdFuncionarioTerc = funcState?.IdFuncionarioTerc ?? filters.IdFuncionarioTerc;
      const filteredcentroDeCusto = funcState?.centroDeCusto ?? filters.centroDeCusto;
      const filteredDocumentosPesquisa = funcState?.DocumentosPesquisa ?? filters.DocumentosPesquisa;
      const filteredAvaliacoesPesquisa = funcState?.AvaliacoesPesquisa ?? filters.AvaliacoesPesquisa;
      const filteredCargoPesquisa = funcState?.CargoPesquisa ?? filters.CargoPesquisa;
      const filteredStatusPesquisa = funcState?.StatusPesquisa ?? filters.StatusPesquisa;
      const filteredContratoPesquisa = funcState?.ContratoPesquisa ?? filters.ContratoPesquisa;
      const filteredFornecedorPesquisa = funcState?.FornecedorPesquisa ?? filters.FornecedorPesquisa;
      const filteredNomePesquisa = funcState?.NomePesquisa ?? filters.NomePesquisa;

      const page = funcState?.page ?? pagination.page;
      const rowsPerPage = funcState?.rowsPerPage ?? pagination.rowsPerPage;

      await getAllListagemFuncionariosTercUseCase.getAllListagemFuncionarioTerc({AvaliacoesPesquisa: filteredAvaliacoesPesquisa, CargoPesquisa: filteredCargoPesquisa, centroDeCusto: filteredcentroDeCusto, ContratoPesquisa: filteredContratoPesquisa, DocumentosPesquisa: filteredDocumentosPesquisa, FornecedorPesquisa: filteredFornecedorPesquisa, NomePesquisa: filteredNomePesquisa, IdFuncionarioTerc: filteredIdFuncionarioTerc, StatusPesquisa: filteredStatusPesquisa},
        page,
        rowsPerPage,
      );
      setFuncionarios(getAllListagemFuncionariosTercUseCase.items);
      setLoading(false);
      pagination.setRowsPerPage(getAllListagemFuncionariosTercUseCase.pageSize);
      pagination.setTotalItems(getAllListagemFuncionariosTercUseCase.totalItems);
    } catch (e: unknown) {
      setLoading(false);
      throw new Error("Falha ao buscar Funcionario Tercerizado!");
    }
  }, [filters.IdFuncionarioTerc, filters.centroDeCusto, filters.DocumentosPesquisa, filters.AvaliacoesPesquisa, filters.CargoPesquisa, filters.StatusPesquisa, filters.ContratoPesquisa, filters.FornecedorPesquisa, filters.NomePesquisa, pagination, getAllListagemFuncionariosTercUseCase]);

  /**
   * useEffects
   */

  usePageStartBootstrap(async () => {
    try {
      setErrorMessage(undefined)
      await loadCentroCustoDropDown();
      await loadFuncionarioTerc();
    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao carregar pagina!")[0]);
    }
  });

  /**
   * Eventos
   */

  const handleReloadPage = useCallback(async () => {
    try {
      setLoading(true);
      await loadFuncionarioTerc();
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao carregar funcionario!"));
    } finally {
      setLoading(false);
    }
  }, [loadFuncionarioTerc, pushErrorNotification]);

  const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
        setLoading(true);
        pagination.setPage(1);
        pagination.setRowsPerPage(rowsPerPage);
        await loadFuncionarioTerc({ page: 1, rowsPerPage });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [loadFuncionarioTerc, pagination, pushErrorNotification]);

  const handlePageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      pagination.setPage(page + 1);;
      await loadFuncionarioTerc({page: page + 1,});
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [loadFuncionarioTerc, pagination, pushErrorNotification]);

  //#region Funções de deletar
    const handleDeleteModalOpenState = useCallback((IdFuncionarioTerc: number | null, openState: boolean) => {
      if (openState) {
        if (IdFuncionarioTerc && funcionarios) {
          setSelectedFuncionario(funcionarios.find(x => x.IdFuncionarioTerc === IdFuncionarioTerc));
        } else {
          setSelectedFuncionario(undefined);
        }
      }
      setDeleteModal(openState);
    }, [funcionarios]);
  
  const handleDeleteConfirma = useCallback(async () => {
    if (selectedFuncionario?.IdFuncionarioTerc) {
      try {
        setSaving(true);
        await deleteFuncionariosTercUseCase.deleteFuncionariosTerc(selectedFuncionario.IdFuncionarioTerc);
        await loadFuncionarioTerc();
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao excluir informações!'));
      } finally {
        setSaving(false);
        setDeleteModal(false);
      }
    }
  },[selectedFuncionario, deleteFuncionariosTercUseCase, loadFuncionarioTerc, pushErrorNotification]);
    
    //#endregion

  //#region Filtros
  const handleClearFilters = useCallback(async () => {
    try {
        setLoading(true);
        filters.clearFilters();
        pagination.setPage(1);
        await loadFuncionarioTerc();
    } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao buscar funcionarios!'));
    } finally {
        setLoading(false);
    }
  }, [filters, loadFuncionarioTerc, pagination, pushErrorNotification]);
    
  const handleFornecedorChange = useDebouncedCallback(debouncedRef, async (newValue: number) => {
    useFuncionariosTercerizacaoFilters.getState().setFornecedorPesquisa(newValue);

    try {
      setLoading(true);
      useFuncionariosTercerizacaoPagination.getState().setPage(1);
      await loadFuncionarioTerc({ FornecedorPesquisa: newValue, page: 1 } as any);
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao carregar funcionarios!"));
    } finally {
      setLoading(false);
    }

    return () => {};
  }, [pushErrorNotification, loadFuncionarioTerc]);
    
  const handleContratoChange = useDebouncedCallback(debouncedRef, async (newValue: number) => {
    useFuncionariosTercerizacaoFilters.getState().setContratoPesquisa(newValue);

    try {
      setLoading(true);
      useFuncionariosTercerizacaoPagination.getState().setPage(1);
      await loadFuncionarioTerc({ ContratoPesquisa: newValue, page: 1 } as any);
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao carregar funcionarios!"));
    } finally {
      setLoading(false);
    }

    return () => {};
  }, [pushErrorNotification, loadFuncionarioTerc]);
    
  const handleCentroDeCustoChange = useDebouncedCallback(debouncedRef, (newValue: number[]) => {
    useFuncionariosTercerizacaoFilters.getState().setCentroDeCusto(newValue);
    return async () => {
      try {
        setLoading(true);
        useFuncionariosTercerizacaoPagination.getState().setPage(1);
        await loadFuncionarioTerc({ centroDeCusto: newValue, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao carregar funcionarios!"));
      } finally {
        setLoading(false);
      }
    }
  }, [ loadFuncionarioTerc, pushErrorNotification, ]);
    
    
  const handleCargoChange = useDebouncedCallback(debouncedRef, (newValue: number) => {
    useFuncionariosTercerizacaoFilters.getState().setCargoPesquisa(newValue);
    return async () => {
      try {
        setLoading(true);
        useFuncionariosTercerizacaoPagination.getState().setPage(1);
        await loadFuncionarioTerc({ CargoPesquisa: newValue, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao carregar funcionarios!"));
      } finally {
        setLoading(false);
      }
    }
  }, [ loadFuncionarioTerc, pushErrorNotification,]);
    
  const handleFornecedorPesquisaChange = useDebouncedCallback(debouncedRef, (newValue: number) => {
    useFuncionariosTercerizacaoFilters.getState().setFornecedorPesquisa(newValue);
    return async () => {
      try {
        setLoading(true);
        useFuncionariosTercerizacaoPagination.getState().setPage(1);
        await loadFuncionarioTerc({ FornecedorPesquisa: newValue, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao carregar funcionarios!"));
      } finally {
        setLoading(false);
      }
    }
  }, [ loadFuncionarioTerc, pushErrorNotification,]);
  
  const handleAvaliacaoPesquisaChange = useDebouncedCallback(debouncedRef, (newValue: number) => {
    useFuncionariosTercerizacaoFilters.getState().setAvaliacoesPesquisa(newValue);
    return async () => {
      try {
        setLoading(true);
        useFuncionariosTercerizacaoPagination.getState().setPage(1);
        await loadFuncionarioTerc({ AvaliacoesPesquisa: newValue, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao carregar funcionarios!"));
      } finally {
        setLoading(false);
      }
    }
  }, [ loadFuncionarioTerc, pushErrorNotification,]);
    
  const handleDocumetosPesquisaChange = useDebouncedCallback(debouncedRef, (newValue: number) => {
    useFuncionariosTercerizacaoFilters.getState().setDocumentosPesquisa(newValue);
    return async () => {
      try {
        setLoading(true);
        useFuncionariosTercerizacaoPagination.getState().setPage(1);
        await loadFuncionarioTerc({ DocumentosPesquisa: newValue, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao carregar funcionarios!"));
      } finally {
        setLoading(false);
      }
    }
  }, [ loadFuncionarioTerc, pushErrorNotification,]);
  
  const handleNomePesquisaChange = useDebouncedCallback(debouncedRef, (newValue: string) => {
    useFuncionariosTercerizacaoFilters.getState().setNomePesquisa(newValue);
    return async () => {
      try {
        setLoading(true);
        useFuncionariosTercerizacaoPagination.getState().setPage(1);
        await loadFuncionarioTerc({ NomePesquisa: newValue, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao carregar funcionarios!"));
      } finally {
        setLoading(false);
      }
    }
  }, [ loadFuncionarioTerc, pushErrorNotification]);
  
  const handleStatusPesquisaChange = useDebouncedCallback(debouncedRef, (newValue: number) => {
    useFuncionariosTercerizacaoFilters.getState().setStatusPesquisa(newValue);
    return async () => {
      try {
        setLoading(true);
        useFuncionariosTercerizacaoPagination.getState().setPage(1);
        await loadFuncionarioTerc({ StatusPesquisa: newValue, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao carregar funcionarios!"));
      } finally {
        setLoading(false);
      }
    }
  }, [ loadFuncionarioTerc, pushErrorNotification]);
  //#endregion
  

  const handleGestaoTreinamentosCadastroOpenState = useCallback(async (idFuncionario: number) => {
    if (idFuncionario) {
      navigate(`/GestaoTreinamentosTerceirizacao_cadastro/${idFuncionario}`);
    }
  }, [
    navigate,
  ]);

  const handleShowLegenda = useCallback((openState: boolean) => {
    setShowLegenda(openState);
  }, [
  ]);

  return {
    loading,
    errorMessage,
    funcionarios: funcionarios ?? [],
    showLegenda,
    filters,
    pagination,
    deleteModal,
    saving,
    selectedFuncionario,
    handleReloadPage,
    handleCentroDeCustoChange,
    handleStatusPesquisaChange,
    handleNomePesquisaChange,
    handleDocumetosPesquisaChange,
    handleAvaliacaoPesquisaChange,
    handleFornecedorPesquisaChange,
    handleFornecedorChange,
    handleContratoChange,
    handleCargoChange,
    handleClearFilters,
    handleGestaoTreinamentosCadastroOpenState,
    handleShowLegenda,
    handlePageChange,
    handleRowsPerPageChange,
    handleDeleteConfirma,
    handleDeleteModalOpenState
  };
}